'use strict';

/**
 * NACRE Wave 2.1.6 — algo-versions registry
 *
 * Single source of truth for algorithm version strings used in encrypted
 * blobs, wrapped keys, signatures, and stored metadata throughout the
 * vault system.
 *
 * Convention:  vN.<part>[.<part>...]
 *   - The leading vN is REQUIRED on every string
 *   - Each part is a lowercase identifier with no dots inside
 *   - Hybrid pairs use + (e.g., x25519+mlkem768)
 *   - Compound strings combine parts with .
 *
 * Examples:
 *   v1.aes256gcm                                    ← symmetric only
 *   v1.x25519+mlkem768                              ← hybrid key exchange only
 *   v1.ed25519+mldsa65                              ← hybrid signature only
 *   v1.argon2id                                     ← KDF only
 *   v1.x25519+mlkem768.aes256gcm.argon2id           ← full vault stack
 *
 * When we add post-quantum hardware-backed keys later, or migrate the
 * symmetric primitive to ChaCha20-Poly1305, etc., we ship a v2 string and
 * old v1 blobs migrate on next access. Versioned everywhere.
 */

// Canonical v1 strings — these are the strings that get embedded in stored
// blobs and metadata. Importing modules should reference the constant, not
// the string literal.
const SYMMETRIC_V1 = 'v1.aes256gcm';
const KEX_V1 = 'v1.x25519+mlkem768';
// T513 / S95 — KEX v2: the hybrid combiner (hybrid-kex.js) now binds the
// transcript (kemCiphertext ‖ x25519EphemeralPublic ‖ recipient public keys)
// into the KDF, X-Wing-style. Same primitives, different KDF input ⇒ a
// different algorithm for versioning purposes: v1 blobs stay READABLE, every
// NEW encapsulation is v2. KEX_CURRENT is the one write-side selector.
const KEX_V2 = 'v2.x25519+mlkem768';
const KEX_CURRENT = KEX_V2;
const SIGN_V1 = 'v1.ed25519+mldsa65';
const KDF_V1 = 'v1.argon2id';
const VAULT_ALGO_V1 = 'v1.x25519+mlkem768.aes256gcm.argon2id';

// T323 — hybrid-PQ algo strings for the surfaces this arc hardens. Each routes
// through hybrid-kex (X25519+ML-KEM-768) so the stored blob carries the ML-KEM
// component in its algo tag (no classical-only bypass).
//   FILE_DEK_HYBRID_V1 — P3: per-recipient file DEK wrap (key-escrow.js)
//   MAIL_HYBRID_V1     — P6: mail private-key escrow blob (master-key-encryption.js)
const FILE_DEK_HYBRID_V1 = 'v1.x25519+mlkem768.aes256kw';
const MAIL_HYBRID_V1 = 'v1.x25519+mlkem768.aes256gcm';
// T513 — the same two families sealed under the v2 (transcript-bound) KEX.
// New writes stamp these; the v1 tags remain readable (at-rest compat).
const FILE_DEK_HYBRID_V2 = 'v2.x25519+mlkem768.aes256kw';
const MAIL_HYBRID_V2 = 'v2.x25519+mlkem768.aes256gcm';
// Family allowlists for readers that route on "is this a hybrid wrap at all"
// (e.g. item-crypto's downgrade refusal) — membership, not a version pin.
const FILE_DEK_HYBRID_ALGOS = Object.freeze([FILE_DEK_HYBRID_V1, FILE_DEK_HYBRID_V2]);
const MAIL_HYBRID_ALGOS = Object.freeze([MAIL_HYBRID_V1, MAIL_HYBRID_V2]);

// T513 — the ONE place the two KEX version namespaces meet. Left: the algo
// string stamped on stored blobs / wire envelopes. Right: the HKDF
// domain-separation label the combiner derives under. hybrid-kex.js reads its
// label from HERE and never spells one, so a blob tagged vN can only ever be
// derived under label vN (the verifier's narrowing 2: bumping only the label
// would leave blobs tagged v1 while deriving under v2). A Map, not a plain
// object, so a hostile version string such as 'constructor' cannot resolve
// through the prototype chain.
const KEX_HKDF_LABELS = new Map([
  [KEX_V1, 'oyster:kex:hybrid:v1'],
  [KEX_V2, 'oyster:kex:hybrid:v2'],
]);

/**
 * HKDF label for a KEX combiner version. Throws on anything not in the
 * allowlist above — never "probably v1".
 * @param {string} version — KEX_V1 | KEX_V2
 * @returns {string}
 */
function kexHkdfLabel(version) {
  const label = KEX_HKDF_LABELS.get(version);
  if (!label) throw new Error('Unsupported KEX version: ' + String(version));
  return label;
}

// T513 — which KEX combiner version a (compound) algo string was sealed under.
// Frozen allowlist: a tag not listed here throws at the read sink.
const KEX_VERSION_OF_ALGO = new Map([
  [KEX_V1, KEX_V1],
  [KEX_V2, KEX_V2],
  [FILE_DEK_HYBRID_V1, KEX_V1],
  [FILE_DEK_HYBRID_V2, KEX_V2],
  [MAIL_HYBRID_V1, KEX_V1],
  [MAIL_HYBRID_V2, KEX_V2],
]);

/**
 * KEX combiner version for a stored/wire algo tag.
 * @param {string} algo
 * @returns {string} KEX_V1 | KEX_V2
 */
function kexVersionForAlgo(algo) {
  const v = KEX_VERSION_OF_ALGO.get(algo);
  if (!v) throw new Error('No hybrid KEX version for algo: ' + String(algo));
  return v;
}

// Legacy / classical-only strings that pre-date this arc. They are NOT preferred
// for new writes, but MUST remain parseable + supported so pre-existing blobs
// stay readable (backward-compat via parseAlgo — custodian-of-ciphertext means
// re-wrap is client-triggered only; LD-21).
const FILE_DEK_CLASSICAL_V1 = 'v1.x25519.aes256kw';

// Set of supported strings. Anything not in this set will fail isSupported().
// When migrations land, future versions can be appended (and a separate
// `migrate()` helper handles the conversion).
const SUPPORTED = new Set([
  SYMMETRIC_V1,
  KEX_V1,
  KEX_V2,
  SIGN_V1,
  KDF_V1,
  VAULT_ALGO_V1,
  FILE_DEK_HYBRID_V1,
  FILE_DEK_HYBRID_V2,
  MAIL_HYBRID_V1,
  MAIL_HYBRID_V2,
  FILE_DEK_CLASSICAL_V1,
]);

/**
 * Parse a versioned algo string into its components.
 *
 * @param {string} algo
 * @returns {{version: number, raw: string, parts: {keyx?: string, sym?: string, kdf?: string, sig?: string}}}
 * @throws if the string is not parseable
 */
function parseAlgo(algo) {
  if (typeof algo !== 'string' || algo.length === 0) {
    throw new Error('Invalid algo: must be a non-empty string');
  }
  if (!algo.startsWith('v')) {
    throw new Error('Invalid algo: must start with version prefix "vN" (got "' + algo + '")');
  }

  // Split on the first dot to separate version from parts
  const dotIdx = algo.indexOf('.');
  if (dotIdx === -1) {
    throw new Error('Invalid algo: missing parts after version prefix');
  }

  const versionStr = algo.slice(1, dotIdx);
  const version = parseInt(versionStr, 10);
  if (!Number.isFinite(version) || version < 1 || String(version) !== versionStr) {
    throw new Error('Invalid algo: version must be a positive integer (got "' + versionStr + '")');
  }

  const partsRaw = algo.slice(dotIdx + 1).split('.');
  const parts = {};
  for (const p of partsRaw) {
    const lower = p.toLowerCase();
    // Categorize each part by recognized prefixes/keywords. The order
    // matters: check the more-specific (hybrid) ones first.
    if (lower.includes('mlkem') || lower.includes('x25519') || lower.includes('p256') || lower.includes('p384')) {
      parts.keyx = lower;
    } else if (lower.includes('mldsa') || lower.includes('ed25519') || lower.includes('ecdsa')) {
      parts.sig = lower;
    } else if (lower.includes('aes') || lower.includes('chacha')) {
      parts.sym = lower;
    } else if (lower.includes('argon') || lower.includes('pbkdf') || lower.includes('scrypt')) {
      parts.kdf = lower;
    } else {
      // Unknown part — preserve it under "extra" so callers can decide
      if (!parts.extra) parts.extra = [];
      parts.extra.push(lower);
    }
  }

  return { version, raw: algo, parts };
}

/**
 * Check if an algo string is currently supported by this build.
 * Returns false for malformed inputs (does not throw).
 *
 * @param {string} algo
 * @returns {boolean}
 */
function isSupported(algo) {
  if (typeof algo !== 'string' || algo.length === 0) return false;
  return SUPPORTED.has(algo);
}

/**
 * List every algo string this build currently supports.
 *
 * @returns {string[]}
 */
function listSupported() {
  return Array.from(SUPPORTED);
}

module.exports = {
  parseAlgo,
  isSupported,
  listSupported,
  kexHkdfLabel,
  kexVersionForAlgo,
  SYMMETRIC_V1,
  KEX_V1,
  KEX_V2,
  KEX_CURRENT,
  SIGN_V1,
  KDF_V1,
  VAULT_ALGO_V1,
  FILE_DEK_HYBRID_V1,
  FILE_DEK_HYBRID_V2,
  FILE_DEK_HYBRID_ALGOS,
  MAIL_HYBRID_V1,
  MAIL_HYBRID_V2,
  MAIL_HYBRID_ALGOS,
  FILE_DEK_CLASSICAL_V1,
};
